import {
  Backdrop,
  Button,
  Container,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Switch,
} from '@mui/material'
import WbButton from '../../../components/common/WbButton'
import ParticipantCard from '../SurveyParticipants/ParticipantCard'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import {useState, useEffect, useMemo} from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import {useNavigate, useOutletContext, useParams} from 'react-router-dom'
import {surveysApi} from '../../../services'
import paths from '../../../routes/paths'
import WbModal from '../../../components/common/WbModal'
import DOMPurify from 'dompurify'
import ChangeForm from './ChangeForm'
import PublishButton from '../../../components/screens/surveyForms/PublishButton'
import ParticipantCountCard from '../SurveyParticipants/ParticipantCountCard'
import useCustomerData from '../../../hooks/useCustomerData'
import {CUSTOMER_TYPE} from '../../../utils/constants'
import {getApiHookParams, normalizeParticipationFilter} from '../../../auth'
import QuestionCategoryBadge from '../../../components/screens/surveyForms/questions/QuestionCategoryBadge'
import {loadBuilderItems} from '../QuestionPanel/loadBuilderItems'
import {isContentScreen, isSkipLogicSection} from '../../../utils/surveyItems'
import {contentScreenTitle} from '../../../utils/contentScreen'
import SkipLogicReviewSummary from '../../../components/screens/surveyForms/items/SkipLogicReviewSummary'
import SurveyPreview from '../../SurveyView/SurveyPreview'
import themesApi from '../../../services/api/themes/themeApi'
import {isSkipLogicBuilderEnabled} from '../../../utils/skipLogicBuilderAccess'

export default function ReviewPanel() {
  const {token} = useParams()
  const customer = useCustomerData()
  const navigate = useNavigate()
  // The review view model has no survey name; the layout shares the loaded
  // survey (which does) through the outlet so the preview header can show it.
  const outlet = useOutletContext()
  const [loadingSurvey, setLoadingSurvey] = useState(true)
  const [surveyData, setSurveyData] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [invite, setInvite] = useState(null)
  // const [staff, setStaff] = useState([])
  // const [parents, setParents] = useState([])
  // const [pupils, setPupils] = useState([])
  const [staffCount, setStaffcount] = useState(0)
  const [parentCount, setParentcount] = useState(0)
  const [studentCount, setStudentCount] = useState(0)
  const [countCards, setCountCards] = useState([])
  const [reviewQuestions, setReviewQuestions] = useState([])
  const [reviewContentScreens, setReviewContentScreens] = useState([])
  const [reviewSkipLogicSections, setReviewSkipLogicSections] = useState([])
  // The full ordered item list, kept intact for the preview (the three arrays
  // above are split by kind and lose the running order the respondent sees).
  const [reviewEntries, setReviewEntries] = useState([])
  const [reviewTheme, setReviewTheme] = useState(null)
  const [preview, setPreview] = useState(false)
  const apiParams = getApiHookParams()
  const groupedReviewQuestions = useMemo(() => {
    const OPEN_QUESTIONS_LABEL = 'Open Questions'
    const groups = {}
    reviewQuestions.forEach(q => {
      const key = q?.categoryName?.trim() || OPEN_QUESTIONS_LABEL
      if (!groups[key]) groups[key] = []
      groups[key].push(q)
    })
    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) => {
        if (a === OPEN_QUESTIONS_LABEL) return 1
        if (b === OPEN_QUESTIONS_LABEL) return -1
        return a.localeCompare(b)
      })
    )
  }, [reviewQuestions])

  useEffect(() => {
    
    setLoadingSurvey(true)
    setLoadError(false)
    surveysApi
      .GetSurveyForReviewByToken(token)
      .then(res => {
        setSurveyData(res)

        // Load the survey's theme so the preview renders in its real colours,
        // the same source the build screen uses. School/MAT come from the auth
        // context when the review VM doesn't carry them; failure just leaves the
        // preview on the default background.
        if (isSkipLogicBuilderEnabled()) {
          themesApi
            .getSurveyThemes(
              res?.id,
              res?.schoolId ?? apiParams?.schoolId,
              res?.matId ?? apiParams?.groupId
            )
            .then(t => {
              if (t) setReviewTheme({...t})
            })
            .catch(() => {})
        }

        setStaffcount(res?.staffCount)
        setParentcount(res?.parentCount)
        setStudentCount(res?.studentCount)

        if (res.isStaffSurvey) {
          setCountCards(prev => [...prev, 'Staff'])
        }
        if (res.isParentSurvey) {
          setCountCards(prev => [...prev, 'Parents'])
        }
        if (res.isPupilSurvey) {
          setCountCards(prev => [...prev, 'Students'])
        }
        // setStaff(res?.participants?.filt
        // er(p => p.audience === 1))
        // setParents(res?.participants?.filter(p => p.audience === 2))
        // setPupils(res?.participants?.filter(p => p.audience === 4))
        setInvite(
          res?.isStaffSurvey
            ? res.staffNotifications
            : res?.isParentSurvey
              ? res.parentNotifications
              : res?.isPupilSurvey
                ? res.pupilNotifications
                : null
        )

        // The item endpoint is keyed by survey id, so load items once the
        // review survey has resolved.
        loadBuilderItems(res?.id, token)
          .then(items => {
            const entries = Array.isArray(items) ? items : []
            setReviewEntries(entries)
            setReviewQuestions(
              entries.filter(q => !isContentScreen(q) && !isSkipLogicSection(q))
            )
            setReviewContentScreens(entries.filter(isContentScreen))
            setReviewSkipLogicSections(entries.filter(isSkipLogicSection))
          })
          .catch(() => {
            setReviewEntries([])
            setReviewQuestions([])
            setReviewContentScreens([])
            setReviewSkipLogicSections([])
          })
      })
      .catch(() => {
        // Without this the failure was indistinguishable from a slow load: surveyData stayed null,
        // the loader cleared anyway, and the page rendered its controls against nothing.
        setLoadError(true)
      })
      .finally(() => setLoadingSurvey(false))
  }, [token])

  const renderH3 = text => <h3 style={{fontWeight: '600'}}>{text}</h3>
  const renderSchedule = () => (
    <Stack spacing={1} mt={1}>
      <div>
        Launch Date/Time:{' '}
        <strong>
          {surveyData?.startDateTime === 'Not Scheduled'
            ? 'Not Scheduled'
            : `${new Date(surveyData?.startDate + 'z').toLocaleDateString('en-gb')}` +
              ' at ' +
              `${surveyData?.startTime}`}
        </strong>
      </div>
      <div>
        Recurrence: <strong>{surveyData?.groupCycleType}</strong>
      </div>
      <div>
        End Date/Time:{' '}
        <strong>
          {surveyData?.endDateTime === 'Not Scheduled'
            ? 'Not Scheduled'
            : `${new Date(surveyData?.endDate + 'z').toLocaleDateString('en-gb')}` +
              ' at ' +
              `${surveyData?.endTime}`}
        </strong>
      </div>
    </Stack>
  )

  const renderEmailTemplate = () => (
    <div className="preview-invitation-modal">
      <div className="preview-edit-btn">
        {renderNavigateEditButton(paths.invitesPanel)}
      </div>
      <div>
        {surveyData?.staffNotifications ? (
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(JSON.parse(invite).Message), //.replace(
              //  '{schoolName}',
              //</Container>customer?.name
              //),
            }}
          ></div>
        ) : (
          <div>No notifications available</div>
        )}
      </div>
    </div>
  )
  const renderChangeform = () => (
    <Grid container>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        {renderH3('Change Survey Settings')}
      </Grid>
      {surveyData.isMatSurvey && (
        <Grid item lg={12} md={12} sm={12} xs={12}>
          {' '}
          Share with Schools
          <Switch
            checked={surveyData?.shareWithSchools === 'Shared'}
            onChange={e => {
              setSurveyData({
                ...surveyData,
                shareWithSchools: e.target.checked ? 'Shared' : 'Not Shared',
              })
            }}
          ></Switch>
        </Grid>
      )}
      <Grid item lg={12} md={12} sm={12} xs={12}>
        Anonymous
        <Switch
          checked={surveyData?.annoymous === 'Fully Anonymous'}
          onChange={e => {
            setSurveyData({
              ...surveyData,
              annoymous: e.target.checked ? 'Fully Anonymous' : 'Not Anonymous',
            })
          }}
        ></Switch>
      </Grid>
      <Grid item lg={6} md={6} sm={12} xs={12}>
        <WbOutlineButton
          CustomButtonText="Cancel"
          onClick={() => {
            setModalType(null)
            setModalOpen(false)
          }}
        ></WbOutlineButton>
      </Grid>
      <Grid item lg={6} md={6} sm={12} xs={12} style={{textAlign: 'right'}}>
        <WbButton
          CustomButtonText="Save"
          onClick={() => {
            surveysApi
              .UpdateAnonymousShareSettings(
                token,
                surveyData?.annoymous === 'Fully Anonymous',
                surveyData?.shareWithSchools === 'Shared'
              )
              .then(res => {
                setSurveyData(res.data)
              })
            setModalType(null)
            setModalOpen(false)
          }}
        ></WbButton>
      </Grid>
    </Grid>
  )

  const renderQuestionCategories = () => (
    <div
      className="view-category-questions"
      style={{maxHeight: '70vh', overflowY: 'auto', padding: '16px'}}
    >
      {reviewQuestions.length === 0 &&
      reviewContentScreens.length === 0 &&
      reviewSkipLogicSections.length === 0 ? (
        <div>No questions to display.</div>
      ) : (
        <>
          {Object.entries(groupedReviewQuestions).map(
            ([categoryName, questions]) => (
              <div key={categoryName} className="review-category-group">
                <div className="review-category-group-title">
                  {categoryName}
                </div>
                {questions.map((q, idx) => (
                  <div
                    key={`${categoryName}-${q.id || idx}`}
                    className="review-question-row"
                  >
                    <div className="review-question-text">
                      {q.staffText || q.parentText || q.pupilText}
                    </div>
                    <QuestionCategoryBadge categoryName={q.categoryName} />
                  </div>
                ))}
              </div>
            )
          )}
          {reviewContentScreens.length > 0 && (
            <div className="review-category-group">
              <div className="review-category-group-title">Content screens</div>
              {reviewContentScreens.map((item, idx) => (
                <div
                  key={`content-${item.itemKey || idx}`}
                  className="review-question-row"
                >
                  <div className="review-question-text">
                    {contentScreenTitle(item.content)}
                  </div>
                  <QuestionCategoryBadge categoryName="Content screen" />
                </div>
              ))}
            </div>
          )}
          <SkipLogicReviewSummary
            survey={{
              items: [
                ...reviewQuestions,
                ...reviewContentScreens,
                ...reviewSkipLogicSections,
              ],
            }}
            sections={reviewSkipLogicSections}
          />
        </>
      )}
    </div>
  )

  const renderNavigateEditButton = path => (
    <IconButton
      disableRipple
      onClick={e => {
        navigate(path.replace(':token', encodeURIComponent(token)))
      }}
      sx={{p: 0}}
      className="review-edit-button"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.83636 12.2319L2.25 15.75L5.76814 15.1636C6.37908 15.0619 6.94294 14.7717 7.38089 14.3337L15.3148 6.39966C15.895 5.81941 15.8951 4.87867 15.3148 4.29844L13.7015 2.68518C13.1212 2.10493 12.1804 2.10494 11.6002 2.6852L3.66631 10.6192C3.22835 11.0572 2.93818 11.621 2.83636 12.2319Z"
          stroke="#6D6D6D"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.5 4.5L13.5 7.5"
          stroke="#6D6D6D"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </IconButton>
  )

  const renderShareAnonymousForm = path => (
    <IconButton
      disableRipple
      onClick={e => {
        setModalType(2)
        setModalOpen(true)
      }}
      sx={{p: 0}}
      className="review-edit-button"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.83636 12.2319L2.25 15.75L5.76814 15.1636C6.37908 15.0619 6.94294 14.7717 7.38089 14.3337L15.3148 6.39966C15.895 5.81941 15.8951 4.87867 15.3148 4.29844L13.7015 2.68518C13.1212 2.10493 12.1804 2.10494 11.6002 2.6852L3.66631 10.6192C3.22835 11.0572 2.93818 11.621 2.83636 12.2319Z"
          stroke="#6D6D6D"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.5 4.5L13.5 7.5"
          stroke="#6D6D6D"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </IconButton>
  )
  if (loadingSurvey)
    return (
      <Container maxWidth="lg">
        <div className="body-container">
          <CircularProgress />
        </div>
      </Container>
    )

  if (loadError || !surveyData)
    return (
      <Container maxWidth="lg">
        <div className="body-container">
          <Stack spacing={2} alignItems="flex-start">
            <h1>Review</h1>
            <div>We could not load this survey. Please try again.</div>
            <WbButton
              CustomButtonText="Retry"
              onClick={() => window.location.reload()}
            ></WbButton>
          </Stack>
        </div>
      </Container>
    )

  return (
    <Container maxWidth="lg">
      <div className="body-container">
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12} mb={4}>
            <Stack
              direction="row"
              alignItems={'center'}
              spacing={2}
              justifyContent={'space-between'}
            >
              <h1>Review</h1>
              <Stack direction="row" spacing={2} alignItems="center">
                {isSkipLogicBuilderEnabled() && reviewEntries.length > 0 && (
                  <WbOutlineButton
                    CustomOutlineButtonText="Preview Survey"
                    onClick={() => setPreview(true)}
                  ></WbOutlineButton>
                )}
                <WbButton
                  CustomButtonText="Preview Invitation"
                  onClick={() => {
                    setModalType(1)
                    setModalOpen(true)
                  }}
                ></WbButton>
              </Stack>
            </Stack>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            {renderH3('Audience')}
          </Grid>{' '}
          <Grid item lg={12} md={12} sm={12} xs={12} mt={1}>
            <div className="participants">
              <Grid container spacing={2} mb={8}>
                {countCards.length > 0 &&
                  countCards.map((type, idx) => (
                    <Grid item lg={4} md={6} sm={12} xs={12}>
                      <ParticipantCountCard
                        key={idx}
                        type={type}
                        studentCount={studentCount}
                        staffCount={staffCount}
                        parentCount={parentCount}
                        participantFilter={{
                          isParentSurvey: surveyData.isParentSurvey,
                          isStaffSurvey: surveyData.isStaffSurvey,
                          isStudentSurvey: surveyData.isPupilSurvey,
                          participationFilter: normalizeParticipationFilter(
                            surveyData.participationFilter,
                            apiParams
                          ),
                          matId:
                            apiParams.groupId > 0 && apiParams.schoolId === null
                              ? apiParams.groupId
                              : apiParams.schoolId !== null && apiParams.group
                                ? apiParams.group.group_id
                                : 0,
                          schoolId:
                            apiParams.schoolId > 0 ? apiParams.schoolId : 0,
                        }}
                      />
                    </Grid>
                  ))}
                {/* {surveyData?.isStaffSurvey && (
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <ParticipantCard
                      type={'Staff'}
                      list={staff}
                      updateCount={() => {}}
                      renderNavigateEditButton={() =>
                        renderNavigateEditButton(paths.surveyParticipants)
                      }
                    />
                  </Grid>
                )}
                {surveyData?.isParentSurvey && (
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <ParticipantCard
                      type={'Parentss'}
                      list={parents}
                      updateCount={() => {}}
                      renderNavigateEditButton={() =>
                        renderNavigateEditButton(paths.surveyParticipants)
                      }
                    />
                  </Grid>
                )}{' '}
                {surveyData?.isPupilSurvey && (
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <ParticipantCard
                      type={'Students'}
                      list={pupils}
                      updateCount={() => {}}
                      renderNavigateEditButton={() =>
                        renderNavigateEditButton(paths.surveyParticipants)
                      }
                    />
                  </Grid>
                )} */}
              </Grid>
            </div>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid container spacing={2}>
              {surveyData?.isMatSurvey && (
                <Grid
                  item
                  lg={3}
                  md={3}
                  sm={6}
                  xs={12}
                  borderRight={1}
                  borderColor={'#e2e2e2'}
                  pb={5}
                  className="preview-section"
                >
                  <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    {renderH3('Share With Schools')}
                    {renderShareAnonymousForm()}
                  </Stack>
                  <div className="review-badge">
                    {surveyData?.shareWithSchools}
                  </div>
                </Grid>
              )}
              <Grid
                item
                lg={3}
                md={3}
                sm={6}
                xs={12}
                borderRight={1}
                borderColor={'#e2e2e2'}
                pb={5}
                className="preview-section"
              >
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                  {renderH3('Anonymity')}
                  {renderShareAnonymousForm()}
                </Stack>
                <div className="review-badge">{surveyData?.annoymous}</div>
              </Grid>
              <Grid
                item
                lg={3}
                md={3}
                sm={6}
                xs={12}
                borderRight={1}
                borderColor={'#e2e2e2'}
                pb={5}
                className="preview-section"
              >
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                  {renderH3('Questions')}
                  {renderNavigateEditButton(paths.questionPanel)}
                </Stack>
                <div className="review-question">
                  No. of Questions:{' '}
                  <span>{surveyData?.questionCount ?? 0}</span>
                </div>
                {reviewContentScreens.length > 0 && (
                  <div className="review-question">
                    Content screens: <span>{reviewContentScreens.length}</span>
                  </div>
                )}
                {reviewSkipLogicSections.length > 0 && (
                  <div className="review-question">
                    Skip-logic sections:{' '}
                    <span>{reviewSkipLogicSections.length}</span>
                  </div>
                )}
                <Stack direction="column" alignItems="flex-start">
                  <Button
                    variant="text"
                    size="small"
                    disableRipple
                    disableElevation
                    className="review-view-questions-button"
                    onClick={e => {
                      navigate(
                        paths.questionPanel.replace(
                          ':token',
                          encodeURIComponent(token)
                        )
                      )
                    }}
                  >
                    View Questions
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    disableRipple
                    disableElevation
                    className="review-view-questions-button"
                    onClick={() => {
                      setModalType(3)
                      setModalOpen(true)
                    }}
                  >
                    View Question Categories
                  </Button>
                </Stack>
              </Grid>
              <Grid
                item
                lg={3}
                md={3}
                sm={6}
                xs={12}
                className="preview-section"
              >
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                  {renderH3('Schedule')}
                  {renderNavigateEditButton(paths.schedulePanel)}
                </Stack>
                {renderSchedule()}
              </Grid>
            </Grid>
          </Grid>

          <Grid item lg={12} md={12} sm={12} xs={12} mt={4}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <WbOutlineButton
                CustomOutlineButtonText={'Previous'}
                onClick={() =>
                  navigate(
                    paths.schedulePanel.replace(
                      ':token',
                      encodeURIComponent(token)
                    )
                  )
                }
              ></WbOutlineButton>
              <div>
                <Stack
                  direction={'row'}
                  spacing={1}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <WbOutlineButton
                    CustomOutlineButtonText={'Cancel'}
                    onClick={() => {
                      navigate(paths.dashboard)
                    }}
                  ></WbOutlineButton>
                  {/* <WbButton
                    CustomButtonText="publish"
                    disabled={loadingSurvey}
                    startIcon={loadingSurvey ? <CircularProgress size={20} /> : null}
                  ></WbButton> */}
                  <PublishButton survey={surveyData} entries={reviewEntries} />
                </Stack>
              </div>
            </Stack>
          </Grid>
        </Grid>
      </div>
      {modalOpen && (
        <WbModal
          title={
            <h3>
              {modalType === 1
                ? 'Preview Invitation'
                : modalType === 3
                  ? 'Question Categories'
                  : 'Change Survey Settings'}
            </h3>
          }
          onClose={() => {
            setModalType(null)
            setModalOpen(false)
          }}
          content={
            modalType === 1 ? (
              renderEmailTemplate()
            ) : modalType === 2 ? (
              <ChangeForm
                surveyData={surveyData}
                setSurveyData={setSurveyData}
                setModalType={setModalType}
                setModalOpen={setModalOpen}
              />
            ) : modalType === 3 ? (
              renderQuestionCategories()
            ) : (
              <div></div>
            )
          }
        ></WbModal>
      )}
      {preview && (
        <SurveyPreview
          entries={reviewEntries}
          audience={
            surveyData?.isStaffSurvey
              ? 1
              : surveyData?.isParentSurvey
                ? 2
                : surveyData?.isPupilSurvey
                  ? 4
                  : 1
          }
          audiences={[
            surveyData?.isStaffSurvey && 1,
            surveyData?.isParentSurvey && 2,
            surveyData?.isPupilSurvey && 4,
          ].filter(Boolean)}
          surveyName={surveyData?.name || outlet?.survey?.name}
          theme={reviewTheme}
          onExit={() => setPreview(false)}
        />
      )}
    </Container>
  )
}
